import React, { useState } from 'react'
import { Form, Input, Button, Checkbox, Card } from 'antd'
import {
  MailOutlined,
  LockOutlined,
  LoginOutlined,
  ExclamationCircleFilled,
} from '@ant-design/icons'
import { useNavigate, Link } from 'react-router-dom'
import logo from '@/assests/Shakthi Power outline logo.png'
import { authStore } from '@/context/auth'
import { api, ApiError } from '@/services/api'
import { notifySuccess } from '@/utils/notify'
import { useMasterData } from '@/context/MasterDataContext'
import { refreshAllStores } from '@/utils/createStore'
import { leadStore } from '@/pages/Lead/leadStore'
import './LoginPage.css'

const LoginPage: React.FC = () => {
  const navigate = useNavigate()
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [authError, setAuthError] = useState('')
  const { refreshMasterData } = useMasterData()

  const onFinish = async (values: { email: string; password: string; remember: boolean }) => {
    setAuthError('')
    setLoading(true)
    try {
      // Always authenticate against the real backend so we store a real JWT.
      // (No offline fallback — a fake token would pass the login screen but be
      //  rejected by the server on every save, silently losing data.)
      const { token, user } = await api.login(values.email, values.password)
      authStore.setToken(token)
      authStore.setUser({ name: user.name, email: user.email, role: user.role, softwareAccess: user.softwareAccess ?? [] })
      // Reads now require a valid token, so the app's very first hydrate (which
      // ran before this token existed) came back empty. Re-fetch everything now
      // that we have a session, so the dashboard doesn't render blank.
      void refreshMasterData()
      void refreshAllStores()
      void leadStore.refresh()
      notifySuccess('Logged in successfully', `Welcome back, ${user.name} 👋`)
      navigate('/select-software')
    } catch (err) {
      setAuthError(
        err instanceof ApiError && err.status === 401
          ? 'Invalid email or password. Please try again.'
          : 'Cannot reach the server. Please check your connection and try again.'
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="lp-root-centered">
      {/* Background glowing gradients */}
      <div className="lp-bg-glow glow-1" />
      <div className="lp-bg-glow glow-2" />
      <div className="lp-bg-glow glow-3" />

      <div className="lp-card-container">
        {/* Logo Container */}
        <div className="lp-logo-header">
          <img src={logo} alt="Shakthi Power Logo" className="lp-logo-main" />
        </div>

        {/* Card component */}
        <Card className="lp-glass-card">
          <div className="lp-card-header">
            <h2>Shakthi Power ERP</h2>
            <p>Enterprise Resource Planning</p>
          </div>

          {authError && (
            <div className="lp-error-alert">
              <ExclamationCircleFilled style={{ fontSize: 16 }} />
              <span>{authError}</span>
            </div>
          )}

          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
            requiredMark={false}
            className="lp-form-dark"
          >
            <Form.Item
              name="email"
              label="Email Address"
              rules={[
                { required: true, message: 'Please enter your email' },
                { type: 'email', message: 'Enter a valid email address' },
              ]}
            >
              <Input
                prefix={<MailOutlined className="input-icon" />}
                placeholder="your@email.com"
                size="large"
              />
            </Form.Item>

            <Form.Item
              name="password"
              label="Password"
              rules={[
                { required: true, message: 'Please enter your password' },
                { min: 6, message: 'Minimum 6 characters' },
              ]}
            >
              <Input.Password
                prefix={<LockOutlined className="input-icon" />}
                placeholder="Enter password"
                size="large"
              />
            </Form.Item>

            <div className="lp-form-options">
              <Form.Item name="remember" valuePropName="checked" noStyle>
                <Checkbox className="lp-checkbox">Remember me</Checkbox>
              </Form.Item>
              <Link to="/forgot-password" className="lp-forgot-link">Forgot Password?</Link>
            </div>

            <Form.Item style={{ marginBottom: 0 }}>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                block
                loading={loading}
                icon={<LoginOutlined />}
                className="lp-btn-submit"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </Form.Item>
          </Form>

        </Card>

        {/* Footer */}
        <div className="lp-footer-text">
          © {new Date().getFullYear()} Shakthi Power ERP. All rights reserved.
        </div>
      </div>
    </div>
  )
}

export default LoginPage
